/**
 * BBCode editor used by the forum and by the administration.
 *
 * The editor is a progressive enhancement of a plain text area: the toolbar
 * wraps the current selection in BBCode tags, the keyboard shortcuts do the
 * same and the preview tab asks the server to render the markup.
 */

import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";
import { postForm, postJson } from "../core/http";
import { toast } from "../core/toast";
import type { PreviewResponse, WikiImageResponse } from "../core/types";
import { validateFiles } from "./upload";

const LOADING_FALLBACK = "Načítavam náhľad…";
const ERROR_FALLBACK = "Náhľad sa nepodarilo načítať, skúste to prosím znova.";
const EMPTY_FALLBACK = "Nie je čo zobraziť, napíšte najprv nejaký text.";

/** Tag pairs inserted by the keyboard shortcuts, keyed by the pressed letter. */
const SHORTCUTS: Readonly<Record<string, readonly [string, string]>> = {
    b: ["[b]", "[/b]"],
    i: ["[i]", "[/i]"],
    u: ["[u]", "[/u]"],
    k: ["[url]", "[/url]"],
};

export interface WrapResult {
    readonly value: string;
    readonly selectionStart: number;
    readonly selectionEnd: number;
}

/**
 * Wrap the range `start`-`end` of `value` in the `open` and `close` tags.
 *
 * An empty range inserts the pair and reports the caret between the two tags,
 * a real selection stays selected inside the freshly inserted tags.
 */
export function applyWrap(value: string, start: number, end: number, open: string, close: string): WrapResult {
    const limit = value.length;
    const from = Math.min(Math.max(Math.min(start, end), 0), limit);
    const to = Math.min(Math.max(Math.max(start, end), 0), limit);

    const before = value.slice(0, from);
    const selected = value.slice(from, to);
    const after = value.slice(to);

    return {
        value: `${before}${open}${selected}${close}${after}`,
        selectionStart: from + open.length,
        selectionEnd: from + open.length + selected.length,
    };
}

/** Wrap the current selection of `field` in the `open` and `close` tags. */
export function wrapSelection(field: HTMLTextAreaElement, open: string, close: string): void {
    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;
    const result = applyWrap(field.value, start, end, open, close);

    field.value = result.value;
    field.focus();
    field.setSelectionRange(result.selectionStart, result.selectionEnd);
    field.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * Insert `text` over the range `start`-`end` of `value`.
 *
 * The caret ends up after what was inserted, which is where somebody who just
 * pasted a picture expects to keep typing.
 */
export function insertAt(value: string, start: number, end: number, text: string): WrapResult {
    const limit = value.length;
    const from = Math.min(Math.max(Math.min(start, end), 0), limit);
    const to = Math.min(Math.max(Math.max(start, end), 0), limit);

    return {
        value: `${value.slice(0, from)}${text}${value.slice(to)}`,
        selectionStart: from + text.length,
        selectionEnd: from + text.length,
    };
}

/** Return the images carried by a paste or a drop, in the order they came. */
function imagesOf(transfer: DataTransfer | null): File[] {
    return Array.from(transfer?.files ?? []).filter((file) => file.type.startsWith("image/"));
}

/**
 * Let a picture be pasted or dropped straight into the body.
 *
 * Only wired when the editor was given an upload address, so the forum keeps
 * behaving exactly as it did. A failed upload inserts nothing at all: there is
 * no placeholder that a second, faster upload could overwrite, and no marker
 * left behind in the text for somebody to wonder about.
 */
function wireUploads(container: HTMLElement, field: HTMLTextAreaElement): void {
    const url = container.dataset["uploadUrl"] ?? "";
    if (!url) {
        return;
    }

    const status = query<HTMLElement>("[data-editor-uploading]", container);

    const announce = (message: string): void => {
        if (status) {
            status.textContent = message;
            status.hidden = message === "";
        }
    };

    const send = async (file: File): Promise<void> => {
        const complaint = validateFiles([file]);
        if (complaint) {
            toast(complaint, "error");

            return;
        }

        announce(text("js.editor.uploading", "Nahrávam obrázok…"));

        const body = new FormData();
        body.append("file", file);

        try {
            const payload = await postForm<WikiImageResponse>(url, body);
            const start = field.selectionStart ?? field.value.length;
            const end = field.selectionEnd ?? start;
            const result = insertAt(field.value, start, end, payload.markup);

            field.value = result.value;
            field.focus();
            field.setSelectionRange(result.selectionStart, result.selectionEnd);
            field.dispatchEvent(new Event("input", { bubbles: true }));
        } catch {
            toast(text("js.editor.upload_failed", "Obrázok sa nepodarilo nahrať."), "error");
        } finally {
            announce("");
        }
    };

    // Which of the two events this is, is asked by the property rather than by
    // the constructor: `ClipboardEvent` is missing from more than one
    // environment the bundle is exercised in, and `instanceof` on a missing
    // global throws rather than answering false.
    const handle = (event: ClipboardEvent | DragEvent): void => {
        const transfer = "clipboardData" in event ? event.clipboardData : event.dataTransfer;
        const images = imagesOf(transfer);

        // Nothing to upload means this is an ordinary paste; leaving it alone
        // is what keeps pasting text working.
        if (images.length === 0) {
            return;
        }

        event.preventDefault();
        for (const file of images) {
            void send(file);
        }
    };

    field.addEventListener("paste", handle);
    field.addEventListener("drop", handle);
}

/** Return the tag pair bound to a keyboard shortcut, or `null`. */
export function shortcutTags(key: string): readonly [string, string] | null {
    return SHORTCUTS[key.toLowerCase()] ?? null;
}

/**
 * Return the further values the preview of `container` is drawn with.
 *
 * Named by the fields they come from, as a selector in `data-preview-with`,
 * and looked for in the form the editor stands in. A field which is disabled,
 * or a box or a choice which is not ticked, sends nothing - exactly as the
 * form itself would not send it.
 */
export function previewExtras(container: HTMLElement): Record<string, string> {
    const selector = container.dataset["previewWith"] ?? "";
    if (!selector) {
        return {};
    }

    const scope: ParentNode = container.closest("form") ?? document;
    const extras: Record<string, string> = {};

    for (const field of queryAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(selector, scope)) {
        if (!field.name || field.disabled) {
            continue;
        }

        if (field instanceof HTMLInputElement && (field.type === "radio" || field.type === "checkbox") && !field.checked) {
            continue;
        }

        extras[field.name] = field.value;
    }

    return extras;
}

/** Wire the toolbar, the shortcuts and the preview of a single editor. */
function initEditor(container: HTMLElement): void {
    const field = query<HTMLTextAreaElement>("[data-editor-textarea]", container);
    if (!field) {
        return;
    }

    const preview = query<HTMLElement>("[data-editor-preview]", container);
    const endpoint = container.dataset["previewUrl"] ?? "";

    let rendered: string | null = null;
    let controller: AbortController | undefined;

    for (const button of queryAll<HTMLElement>("[data-editor-button]", container)) {
        button.addEventListener("click", (event: Event) => {
            event.preventDefault();

            const open = button.dataset["open"] ?? "";
            const close = button.dataset["close"] ?? "";

            if (!open && !close) {
                return;
            }

            wrapSelection(field, open, close);
        });
    }

    field.addEventListener("keydown", (event: KeyboardEvent) => {
        if (!event.ctrlKey && !event.metaKey) {
            return;
        }

        if (event.altKey) {
            return;
        }

        const tags = shortcutTags(event.key);
        if (!tags) {
            return;
        }

        event.preventDefault();
        wrapSelection(field, tags[0], tags[1]);
    });

    const renderPreview = (): void => {
        if (!preview || !endpoint) {
            return;
        }

        const content = field.value;
        const payload = { content, ...previewExtras(container) };
        // What was drawn is remembered with everything it was drawn from, so
        // changing the subject of a letter draws the preview again.
        const drawing = JSON.stringify(payload);

        if (content.trim().length === 0) {
            rendered = drawing;
            preview.textContent = text("js.editor.empty", EMPTY_FALLBACK);

            return;
        }

        if (rendered === drawing) {
            return;
        }

        preview.textContent = text("js.editor.loading", LOADING_FALLBACK);
        controller?.abort();
        controller = new AbortController();

        void postJson<PreviewResponse>(endpoint, payload, controller.signal)
            .then((answer) => {
                rendered = drawing;
                preview.innerHTML = answer.html;
            })
            .catch((error: unknown) => {
                if (error instanceof DOMException && error.name === "AbortError") {
                    return;
                }

                rendered = null;
                preview.textContent = "";
                toast(text("js.editor.error", ERROR_FALLBACK), "error");
            });
    };

    const tabs = queryAll<HTMLElement>("[data-editor-tab]", container);

    const setMode = (mode: string, moveFocus = true): void => {
        const previewing = mode === "preview";

        container.classList.toggle("is-preview", previewing);
        field.hidden = previewing;

        if (preview) {
            preview.hidden = !previewing;
        }

        for (const tab of tabs) {
            const active = (tab.dataset["editorTab"] ?? "") === mode;
            tab.classList.toggle("is-active", active);
            tab.setAttribute("aria-selected", active ? "true" : "false");
        }

        if (previewing) {
            renderPreview();
        } else if (moveFocus) {
            field.focus();
        }
    };

    for (const tab of tabs) {
        tab.addEventListener("click", (event: Event) => {
            event.preventDefault();
            setMode(tab.dataset["editorTab"] ?? "write");
        });
    }

    if (tabs.length > 0) {
        setMode("write", false);
    }

    wireUploads(container, field);
}

/** Wire every BBCode editor present on the page. */
export function initEditors(): void {
    for (const container of queryAll<HTMLElement>("[data-editor]")) {
        initEditor(container);
    }
}
